import { BookOpenIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";
import styled from "styled-components";

import { redactLoreTerms } from "src/shared/utils/loreReveal";
import {
  LumaBanner0Img,
  LumaBanner100Img,
  LumaBanner25Img,
  LumaBanner50Img,
  LumaBanner75Img,
} from "src/static/img";

import { getHackingCountdown, LoreStage } from "./dashboardUtils";

type HackerHeroProps = {
  lore: LoreStage;
  now: Date;
};

type LumaBanner = {
  desktopAnchorX: number;
  image: string;
  mobileToggleColor: string;
  mobileAnchorX: number;
  stageColor: string;
};

const LUMA_BANNER_WIDTH = 4600;

// Source-image x-coordinates that stay aligned with each frame's center.
// Increase an anchor to move the artwork left; decrease it to move it right.
const LUMA_BANNERS: Record<number, LumaBanner> = {
  0: {
    desktopAnchorX: 2300,
    image: LumaBanner0Img,
    mobileAnchorX: 2391,
    mobileToggleColor: "65, 62, 16",
    stageColor: "65, 62, 16",
  },
  25: {
    desktopAnchorX: 2300,
    image: LumaBanner25Img,
    mobileAnchorX: 2391,
    mobileToggleColor: "28, 55, 10",
    stageColor: "28, 55, 10",
  },
  50: {
    desktopAnchorX: 2300,
    image: LumaBanner50Img,
    mobileAnchorX: 2391,
    mobileToggleColor: "28, 55, 10",
    stageColor: "3, 22, 43",
  },
  75: {
    desktopAnchorX: 2300,
    image: LumaBanner75Img,
    mobileAnchorX: 2251,
    mobileToggleColor: "1, 51, 62",
    stageColor: "1, 51, 62",
  },
  100: {
    desktopAnchorX: 2300,
    image: LumaBanner100Img,
    mobileAnchorX: 2147,
    mobileToggleColor: "36, 0, 41",
    stageColor: "36, 0, 41",
  },
};

const HackerHero: React.FC<HackerHeroProps> = ({ lore, now }) => {
  const [isLoreExpanded, setIsLoreExpanded] = useState(false);
  const countdown = getHackingCountdown(now);
  const banner = LUMA_BANNERS[lore.threshold] ?? LUMA_BANNERS[0];
  const displayedLumaName = redactLoreTerms("Luma", now);

  return (
    <HeroSection>
      <HeroScene
        aria-label={`Hack the North ${displayedLumaName} landscape at ${lore.threshold}% restored`}
      >
        <HeroArtwork
          $desktopAnchorX={banner.desktopAnchorX}
          $mobileAnchorX={banner.mobileAnchorX}
          src={banner.image}
          alt=""
          aria-hidden="true"
        />
        <HeroOverlay>
          <CountdownCard $stageColor={banner.stageColor} aria-live="off">
            <CountdownLabel>{countdown.label}</CountdownLabel>
            <CountdownValue>{countdown.value}</CountdownValue>
          </CountdownCard>
          <DesktopLoreCard $stageColor={banner.stageColor}>
            <LoreTitle>{lore.title}</LoreTitle>
            <DesktopLoreBody>{lore.body}</DesktopLoreBody>
          </DesktopLoreCard>
        </HeroOverlay>
        <LoreToggle
          type="button"
          aria-expanded={isLoreExpanded}
          aria-controls="mobile-luma-lore"
          aria-label={`${
            isLoreExpanded ? "Hide" : "Show"
          } ${displayedLumaName} lore`}
          onClick={() => setIsLoreExpanded((expanded) => !expanded)}
          $stageColor={banner.mobileToggleColor}
        >
          <BookOpenIcon aria-hidden="true" />
        </LoreToggle>
      </HeroScene>
      {isLoreExpanded && (
        <MobileLoreCard $stageColor={banner.stageColor} id="mobile-luma-lore">
          <LoreTitle>{lore.title}</LoreTitle>
          <MobileLoreBody>{lore.body}</MobileLoreBody>
        </MobileLoreCard>
      )}
    </HeroSection>
  );
};

export default HackerHero;

const HeroSection = styled.section`
  width: 100%;
`;

const HeroScene = styled.div`
  position: relative;
  z-index: 0;
  height: 340px;
  overflow: hidden;

  ${({ theme }) => theme.mediaQueries.tablet`
    height: 295px;
  `}
`;

const HeroArtwork = styled.img<{
  $desktopAnchorX: number;
  $mobileAnchorX: number;
}>`
  position: absolute;
  top: -1px;
  left: 50%;
  display: block;
  width: auto;
  max-width: none;
  height: calc(100% + 2px);
  pointer-events: none;
  user-select: none;
  transform: translateX(
    -${({ $desktopAnchorX }) => ($desktopAnchorX / LUMA_BANNER_WIDTH) * 100}%
  );

  ${({ $mobileAnchorX, theme }) => theme.mediaQueries.tablet`
    top: 0;
    height: 100%;
    transform: translateX(-${($mobileAnchorX / LUMA_BANNER_WIDTH) * 100}%);
  `}
`;

const HeroOverlay = styled.div`
  position: absolute;
  right: 32px;
  bottom: 24px;
  left: 24px;
  z-index: 1;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;

  ${({ theme }) => theme.mediaQueries.tablet`
    top: 16px;
    right: 16px;
    bottom: 50px;
    left: 16px;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
  `}
`;

const CountdownCard = styled.div<{ $stageColor: string }>`
  flex: 0 0 auto;
  padding: 8px 16px;
  border-radius: 8px;
  background: rgba(${({ $stageColor }) => $stageColor}, 0.6);
  color: #ffffff;
  font-style: normal;
  text-align: left;

  ${({ theme }) => theme.mediaQueries.tablet`
    text-align: center;
  `}
`;

const CountdownLabel = styled.p`
  margin: 0 0 -8px;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: 0.8px;
  text-transform: uppercase;
`;

const CountdownValue = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-size: 42px;
  font-weight: 900;
  line-height: normal;
  white-space: nowrap;
`;

const DesktopLoreCard = styled.div<{ $stageColor: string }>`
  width: 455px;
  max-width: 50%;
  padding: 8px 16px;
  border-radius: 8px;
  background: rgba(${({ $stageColor }) => $stageColor}, 0.6);
  color: #ffffff;

  ${({ theme }) => theme.mediaQueries.tablet`
    display: none;
  `}
`;

const LoreTitle = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-size: 20px;
  font-weight: 900;
  line-height: normal;
`;

const DesktopLoreBody = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  font-weight: 500;
  line-height: normal;
`;

const LoreToggle = styled.button<{ $stageColor: string }>`
  position: absolute;
  right: 16px;
  bottom: 16px;
  z-index: 2;
  display: none;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 5px;
  border: 0;
  border-radius: 50%;
  background: rgba(${({ $stageColor }) => $stageColor}, 0.6);
  color: #ffffff;
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.globalConstants.color.focus};
    outline-offset: 2px;
  }

  ${({ theme }) => theme.mediaQueries.tablet`
    display: flex;
  `}
`;

const MobileLoreCard = styled.div<{ $stageColor: string }>`
  display: none;
  background: rgb(${({ $stageColor }) => $stageColor});
  color: #ffffff;

  ${({ theme }) => theme.mediaQueries.tablet`
    display: block;
    margin: 16px 40px 0;
    padding: 8px 16px;
    border-radius: 8px;

    ${LoreTitle} {
      font-family: ${theme.globalConstants.fontFamily.body};
      font-size: 16px;
      font-weight: 700;
      line-height: normal;
    }
  `}

  ${({ theme }) => theme.mediaQueries.largeMobile`
    margin-right: 24px;
    margin-left: 24px;
  `}

  ${({ theme }) => theme.mediaQueries.smallMobile`
    margin-right: 20px;
    margin-left: 20px;
  `}
`;

const MobileLoreBody = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
`;
